import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import LandingPageRenderer, { hasStatusPanel } from '../../Portal/LandingPageRenderer';
import { AUTH_METHOD_REGISTRY } from '../../Portal/authRegistry';
import FrameWidthHandle from './FrameWidthHandle';

/**
 * Builder "Live Preview" — renders the page's CURRENT (unsaved) node tree
 * through the very same LandingPageRenderer the guest portal uses, so what
 * you see here is the real design (real login forms, real colours/layout),
 * not the edit canvas's structural placeholders.
 *
 * It runs inside an IFRAME of the chosen width, not a plain div: CSS media
 * queries look at the size of the window they are in, so only an iframe gives
 * the page the same @media behaviour a device of that width would — for every
 * size class, and for the login forms' own media queries too.
 *
 * Everything the renderer normally gets from a real branch/guest session is
 * stubbed with harmless sample data: nothing here calls the API, sends an
 * OTP, or logs anyone in.
 */

// Every method the guest portal can render — the preview pretends a branch
// with all of them enabled, so any login component you place shows up.
const PREVIEW_METHODS = ['pms', 'otp', 'voucher', 'resident', 'staff'];

// Show the first login form the page actually contains by default — with
// only an OTP form on the page, starting on the PMS tab would preview a
// blank page until the tester happened to click the right tab.
function firstAuthUserType(nodes) {
    for (const node of nodes || []) {
        const method = node.authRef?.method;
        if (method && AUTH_METHOD_REGISTRY[method]) return AUTH_METHOD_REGISTRY[method].userType;
        const inner = firstAuthUserType(node.children);
        if (inner) return inner;
    }
    return null;
}

// What the page's status box shows after sign-in, with sample data — picked in the
// preview's toolbar so the screens can be checked without signing anyone in.
const SAMPLE_STATUS = {
    connected: {
        kind: 'connected',
        onContinue: () => {},
        details: [
            { label: 'Signed in as', value: '204' },
            { label: 'Network', value: 'Your Hotel Name' },
            { label: 'Device', value: 'AA:BB:CC:11:22:33' },
            { label: 'IP address', value: '192.168.10.24' },
        ],
    },
    error: { kind: 'error', message: 'The sign-in system didn’t respond. Wait a moment and try again.' },
    // "My Wi-Fi": the guest back on the portal while connected, and after Disconnect.
    online: {
        kind: 'online', onContinue: () => {}, onDisconnect: () => {},
        details: [
            { label: 'Signed in as', value: '204' },
            { label: 'Time online', value: '1h 12m 38s' },
            { label: 'Downloaded', value: '612.9 MB' },
            { label: 'Uploaded', value: '38.4 MB' },
        ],
    },
    disconnected: {
        kind: 'disconnected', onConnectAgain: () => {},
        details: [
            { label: 'Signed in as', value: '204' },
            { label: 'Time online', value: '1h 12m 38s' },
            { label: 'Downloaded', value: '612.9 MB' },
            { label: 'Uploaded', value: '38.4 MB' },
        ],
    },
};

const BLANK_DOCUMENT = '<!doctype html><html><head></head><body></body></html>';

/** Renders `children` into an iframe's document, with the app's own stylesheets copied in. */
function PreviewFrame({ children }) {
    const iframeRef = useRef(null);
    const mountRef = useRef(null);
    const [mount, setMount] = useState(null);

    // Runs on mount and again on the iframe's load event (browsers may swap the
    // initial blank document for the srcDoc one — a fresh mount is made then).
    const init = useCallback(() => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc || !doc.body) return;
        if (mountRef.current && mountRef.current.ownerDocument === doc && doc.body.contains(mountRef.current)) return;

        doc.head.querySelectorAll('[data-lb-copied]').forEach((node) => node.remove());
        document.head.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
            const copy = node.cloneNode(true);
            copy.setAttribute('data-lb-copied', '1');
            doc.head.appendChild(copy);
        });
        doc.body.style.margin = '0';

        const node = doc.createElement('div');
        doc.body.appendChild(node);
        mountRef.current = node;
        setMount(node);
    }, []);

    useEffect(() => { init(); }, [init]);

    return (
        <>
            <iframe
                ref={iframeRef}
                title="Live preview"
                className="lb-preview-iframe"
                srcDoc={BLANK_DOCUMENT}
                onLoad={init}
            />
            {mount && createPortal(children, mount)}
        </>
    );
}

export default function LivePreview({ nodes, frameWidth = '100%', onWidthChange }) {
    const [userType, setUserType] = useState(() => firstAuthUserType(nodes) || 'guest');
    const [formData, setFormData] = useState({});
    const [odxLoginType, setOdxLoginType] = useState('room');
    const [showGenericPw, setShowGenericPw] = useState(false);
    const [otpMethod, setOtpMethod] = useState('whatsapp');
    const [otpSent, setOtpSent] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [screen, setScreen] = useState('signin'); // 'signin' | 'connected' | 'error'
    const showsStatus = hasStatusPanel(nodes);

    const portalData = useMemo(() => ({
        loginMethods: PREVIEW_METHODS,
        organizationType: 'hotel',
        pmsProvider: null,
        branchName: 'Your Hotel Name',
        welcomeMessage: 'Welcome to our WiFi',
        termsAndConditions: '',
        redirectURL: '',
        logo: null,
        methodLabels: null,
    }), []);

    const handleInputChange = (e) => {
        const { name, type, checked, value } = e.target;
        setFormData((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    };

    const authContext = {
        formData, handleInputChange,
        handleSubmit: (e) => e.preventDefault(),
        userType, setUserType,
        odxLoginType, setOdxLoginType,
        showGenericPw, setShowGenericPw,
        otpMethod, setOtpMethod, otpSent, otpTimer: 0,
        handleSendOTP: () => setOtpSent(true),
        loading: false,
        showTermsModal, setShowTermsModal,
        loginError: '',
        status: showsStatus ? SAMPLE_STATUS[screen] || null : null,
        clearStatus: () => setScreen('signin'),
    };

    return (
        <div className="lb-preview-wrap">
            <p className="lb-preview-note">
                <i className="fas fa-eye"></i> Live preview of your current changes at the chosen device width — sample hotel data, and the login forms don&apos;t submit anything.
            </p>
            {showsStatus && (
                <label className="lb-preview-screen">
                    Screen{' '}
                    <select value={screen} onChange={(e) => setScreen(e.target.value)} aria-label="Preview screen">
                        <option value="signin">Sign-in</option>
                        <option value="connected">Connected</option>
                        <option value="error">Can&apos;t connect</option>
                        <option value="online">My Wi-Fi (connected)</option>
                        <option value="disconnected">Disconnected</option>
                    </select>
                </label>
            )}
            <div className="lb-preview-frame lb-frame" style={{ width: frameWidth }}>
                <PreviewFrame>
                    <LandingPageRenderer nodes={nodes} portalData={portalData} authContext={authContext} />
                </PreviewFrame>
                {onWidthChange && <FrameWidthHandle onWidthChange={onWidthChange} maxWidth={2560} />}
            </div>
        </div>
    );
}
